import { accountPayableApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  GetAccountPayableParamsI,
  ListAccountPayableI,
  OneAccountPayableForFrontI,
  NewAccountPayableI,
  RegisterPaymentApI,
} from "../../interfaces";
import { AxiosError } from "axios";

const getAccountPayables = async ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  supplierId,
  creditorId,
  statusCode,
  dateFrom,
  dateTo,
}: GetAccountPayableParamsI): Promise<ServiceResponseI<DataI<ListAccountPayableI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page",    (pagination.pageIndex + 1).toString());
    params.append("limit",   pagination.pageSize.toString());
    params.append("search",  globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (branchId)   params.append("branchId",   branchId);
    if (supplierId) params.append("supplierId", supplierId);
    if (creditorId) params.append("creditorId", creditorId);
    if (statusCode) params.append("statusCode", statusCode);
    if (dateFrom)   params.append("dateFrom",   dateFrom);
    if (dateTo)     params.append("dateTo",     dateTo);

    const { data } = await accountPayableApi.get<ServiceResponseI<DataI<ListAccountPayableI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const accountPayableById = async (
  id: string,
): Promise<ServiceResponseI<OneAccountPayableForFrontI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await accountPayableApi.get<ServiceResponseI<OneAccountPayableForFrontI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const accountPayablePost = async (
  payload: NewAccountPayableI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await accountPayableApi.post<ServiceResponseI<string>>(
      "",
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const accountPayableRegisterPayment = async (
  payload: RegisterPaymentApI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await accountPayableApi.post<ServiceResponseI<string>>(
      "/payment",
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const accountPayableAnnul = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await accountPayableApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useAccountPayables = ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  supplierId,
  creditorId,
  statusCode,
  dateFrom,
  dateTo,
}: GetAccountPayableParamsI) => {
  const accountPayablesQuery = useQuery(
    ["account-payables", { pagination, sorting, globalFilter, branchId, supplierId, creditorId, statusCode, dateFrom, dateTo }],
    () => getAccountPayables({ pagination, sorting, globalFilter, branchId, supplierId, creditorId, statusCode, dateFrom, dateTo }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData:     true,
    },
  );

  return { accountPayablesQuery };
};